export const DEFAULT_OPERATION_DATE_RANGE_KEY = "last_7_days";

const OCC_MONTH_LABELS = Object.freeze(["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]);

const OPERATION_DATE_RANGE_DEFINITIONS = Object.freeze([
    { value: "last_7_days", label: "近 7 天", startOffsetDays: -6, endOffsetDays: 0 },
    { value: "last_14_days", label: "近 14 天", startOffsetDays: -13, endOffsetDays: 0 },
    { value: "month_to_date", label: "本月至今", range: "month_to_date" },
]);

function buildStatusOptions(values) {
    return Object.freeze(values.map((value) => ({ value, label: value })));
}

function normalizeStatusEditValue(value) {
    if (value === undefined || value === "") return null;
    return value;
}

const FIXED_OPERATION_FIELD_KEYS = new Set(["status", "remarks"]);
const DEFAULT_STATUS_OPTIONS = buildStatusOptions(["PENDING", "APPROVED", "REJECTED", "CANCELLED"]);

const DEFAULT_OPERATION_CATEGORY_DEFINITIONS = Object.freeze([
    { category: "fuel", label: "加油申请", order: 10, statusOptions: DEFAULT_STATUS_OPTIONS },
    { category: "permit", label: "许可申请", order: 20, statusOptions: DEFAULT_STATUS_OPTIONS, allowClearStatus: true },
    { category: "catering", label: "配餐订单", order: 30, statusOptions: buildStatusOptions(["DRAFT", "PENDING", "APPROVED", "REJECTED", "CANCELLED"]) },
    { category: "brief", label: "飞行简报", order: 40, statusOptions: DEFAULT_STATUS_OPTIONS },
    { category: "crew_transport", label: "机组交通", order: 50, statusOptions: buildStatusOptions(["PENDING", "APPROVED", "CANCELLED"]) },
    { category: "hotac", label: "住宿", order: 60, statusOptions: buildStatusOptions(["PENDING", "APPROVED", "CANCELLED"]) },
]);

const DEFAULT_OPERATION_CATEGORY_BY_KEY = Object.freeze(
    DEFAULT_OPERATION_CATEGORY_DEFINITIONS.reduce((lookup, definition) => {
        lookup[definition.category] = definition;
        return lookup;
    }, {}),
);

export const DEFAULT_OPERATION_FIELD_CONFIG = Object.freeze(
    DEFAULT_OPERATION_CATEGORY_DEFINITIONS.reduce((config, definition) => {
        config[definition.category] = Object.freeze({
            label: definition.label,
            order: definition.order,
            fields: Object.freeze([]),
        });
        return config;
    }, {}),
);

export const OPERATION_ITEM_STATUS_OPTIONS = Object.freeze(
    DEFAULT_OPERATION_CATEGORY_DEFINITIONS.reduce((options, definition) => {
        options[definition.category] = definition.statusOptions;
        return options;
    }, {}),
);

function buildStatusColumn(category, width) {
    const defaultDefinition = DEFAULT_OPERATION_CATEGORY_BY_KEY[category];
    const allowClear = defaultDefinition?.allowClearStatus === true;
    const selectOptions = OPERATION_ITEM_STATUS_OPTIONS[category] || DEFAULT_STATUS_OPTIONS;
    return {
        key: "status",
        label: "Status",
        width,
        signalAware: true,
        editable: true,
        editorType: "select",
        options: selectOptions,
        editorOptions: {
            selectOptions,
            allowClear,
        },
        allowClear,
        normalize: normalizeStatusEditValue,
    };
}

function buildRemarksColumn(width = 160) {
    return {
        key: "remarks",
        label: "Remarks",
        width,
        editable: true,
        editorType: "textarea",
        placeholder: "",
    };
}

function buildAttributeColumn(field) {
    return {
        key: field.key,
        label: field.label || field.key,
        width: field.width || 128,
        minWidth: field.minWidth,
        editable: field.editable !== false,
        editorType: "text",
        placeholder: field.placeholder || "",
        attribute: true,
    };
}

function toUtcDateOnly(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addUtcDays(date, days) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));
}

function formatUtcDate(date) {
    return date.toISOString().slice(0, 10);
}

export function buildOperationDateRangeOptions(referenceDate = new Date()) {
    const today = toUtcDateOnly(referenceDate);

    return OPERATION_DATE_RANGE_DEFINITIONS.map((definition) => {
        if (definition.range === "month_to_date") {
            const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
            return {
                value: definition.value,
                label: definition.label,
                date_from: formatUtcDate(monthStart),
                date_to: formatUtcDate(today),
            };
        }

        return {
            value: definition.value,
            label: definition.label,
            date_from: formatUtcDate(addUtcDays(today, definition.startOffsetDays)),
            date_to: formatUtcDate(addUtcDays(today, definition.endOffsetDays)),
        };
    });
}

function isPlainObject(value) {
    return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function normalizeText(value) {
    return typeof value === "string" ? value.trim() : "";
}

function normalizeFiniteNumber(value, fallback) {
    if (value === null || value === undefined || value === "") return fallback;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
}

function formatCategoryLabel(category) {
    return String(category)
        .split("_")
        .filter(Boolean)
        .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
        .join(" ") || String(category);
}

function looksLikeFieldConfig(value) {
    if (!isPlainObject(value)) return false;
    return Object.values(value).some((entry) => isPlainObject(entry) && (Array.isArray(entry.fields) || entry.label !== undefined));
}

function extractOperationFieldConfigValue(source) {
    if (looksLikeFieldConfig(source)) return source;

    const preferenceItems = Array.isArray(source) ? source : source?.items;
    if (!Array.isArray(preferenceItems) || preferenceItems.length === 0) return null;

    const preferredItem = preferenceItems.find(
        (item) => ["fieldConfig", "default", "categories"].includes(item?.key) && looksLikeFieldConfig(item?.value),
    );
    if (preferredItem) return preferredItem.value;

    if (preferenceItems.length === 1 && looksLikeFieldConfig(preferenceItems[0]?.value)) {
        return preferenceItems[0].value;
    }

    const categoryConfig = preferenceItems.reduce((config, item) => {
        const category = normalizeText(item?.key);
        if (category && isPlainObject(item?.value)) config[category] = item.value;
        return config;
    }, {});

    return Object.keys(categoryConfig).length > 0 ? categoryConfig : null;
}

function normalizeAttributeFields(fields) {
    if (!Array.isArray(fields)) return [];

    const seenKeys = new Set();
    return fields.reduce((normalizedFields, field) => {
        if (!isPlainObject(field)) return normalizedFields;

        const key = normalizeText(field.key);
        if (!key || FIXED_OPERATION_FIELD_KEYS.has(key) || seenKeys.has(key)) return normalizedFields;
        seenKeys.add(key);

        normalizedFields.push({
            key,
            label: normalizeText(field.label) || key,
            width: normalizeFiniteNumber(field.width, 128),
            minWidth: field.minWidth === undefined ? undefined : normalizeFiniteNumber(field.minWidth, undefined),
            editable: field.editable !== false,
            placeholder: normalizeText(field.placeholder),
        });
        return normalizedFields;
    }, []);
}

function mergeOperationFieldConfig(rawConfig) {
    const mergedConfig = DEFAULT_OPERATION_CATEGORY_DEFINITIONS.reduce((config, definition) => {
        config[definition.category] = {
            label: definition.label,
            order: definition.order,
            visible: true,
            fields: [],
        };
        return config;
    }, {});

    if (!isPlainObject(rawConfig)) return mergedConfig;

    Object.entries(rawConfig).forEach(([rawCategory, categoryConfig], index) => {
        const category = normalizeText(rawCategory);
        if (!category) return;

        const defaultDefinition = DEFAULT_OPERATION_CATEGORY_BY_KEY[category];
        const defaultConfig = mergedConfig[category] || {
            label: defaultDefinition?.label || formatCategoryLabel(category),
            order: defaultDefinition?.order || 1000 + index * 10,
            visible: true,
            fields: [],
        };
        const configObject = isPlainObject(categoryConfig) ? categoryConfig : {};

        mergedConfig[category] = {
            label: normalizeText(configObject.label) || defaultConfig.label,
            order: normalizeFiniteNumber(configObject.order, defaultConfig.order),
            visible: configObject.visible !== false,
            fields: normalizeAttributeFields(configObject.fields),
        };
    });

    return mergedConfig;
}

export function resolveOperationFieldConfig(preferencesOrConfig) {
    return mergeOperationFieldConfig(extractOperationFieldConfigValue(preferencesOrConfig));
}

export function buildOperationColumnBlocks(fieldConfig = DEFAULT_OPERATION_FIELD_CONFIG) {
    const normalizedConfig = mergeOperationFieldConfig(fieldConfig);
    return Object.entries(normalizedConfig)
        .map(([category, config], index) => ({ category, config, index }))
        .sort((left, right) => (left.config.order || 0) - (right.config.order || 0) || left.index - right.index)
        .map(({ category, config }) => ({
            blockId: category,
            label: config.label || formatCategoryLabel(category),
            category,
            transactionType: category,
            visible: config.visible !== false,
            order: config.order,
            columns: [
                buildStatusColumn(category, category === "fuel" ? 104 : 112),
                buildRemarksColumn(),
                ...normalizeAttributeFields(config.fields).map(buildAttributeColumn),
            ],
        }));
}

export const OPERATION_COLUMN_BLOCKS = Object.freeze(buildOperationColumnBlocks(DEFAULT_OPERATION_FIELD_CONFIG));

export const OPERATION_CORE_COLUMNS = Object.freeze([
    { key: "day", label: "Day", width: 56, minWidth: 48, editable: false },
    { key: "flight", label: "Flight", width: 88, minWidth: 76, editable: false },
    { key: "dep", label: "DEP", width: 64, minWidth: 56, editable: false },
    { key: "arr", label: "ARR", width: 64, minWidth: 56, editable: false },
    { key: "std", label: "STD", width: 72, minWidth: 64, cellType: "time", editable: false, sortBy: "departure" },
    { key: "sta", label: "STA", width: 72, minWidth: 64, cellType: "time", editable: false, sortBy: "arrival" },
    { key: "status", label: "Status", width: 104, minWidth: 88, editable: false },
    { key: "bh", label: "BH", width: 72, minWidth: 64, editable: false },
    { key: "gt", label: "GT", width: 80, minWidth: 72, editable: false },
    { key: "remark", label: "Remark", width: 180, minWidth: 140, placeholder: "", editable: false },
]);

export const OPERATION_SORTABLE_COLUMNS = Object.freeze(
    OPERATION_CORE_COLUMNS
        .filter((column) => column.sortBy)
        .map((column) => ({ columnKey: column.key, sortBy: column.sortBy })),
);

export function getDateRangeOption(value, options = buildOperationDateRangeOptions()) {
    return options.find((option) => option.value === value) || options[0];
}

export function formatOperationDate(date) {
    const parts = parseUtcDateParts(date);
    if (!parts) return "-";
    return `${parts.day}-${OCC_MONTH_LABELS[parts.month - 1]}`;
}

export function formatOperationLegLabel(item) {
    return `${item.flight_no || "-"} ${item.dep || "-"}->${item.arr || "-"}`;
}

export function formatOperationDay(date) {
    const parts = parseUtcDateParts(date);
    if (!parts) return "";
    const day = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
    return String(day === 0 ? 7 : day);
}

export function formatOperationTime(time) {
    if (!time || typeof time !== "string") return "";
    const date = new Date(time);
    if (Number.isNaN(date.getTime())) return "";
    return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
}

export function formatBlockTimeMinutes(value) {
    const minutes = normalizeMinutes(value);
    if (minutes === null || minutes < 0) return "";
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export function formatGroundTimeMinutes(value) {
    const minutes = normalizeMinutes(value);
    if (minutes === null) return "";

    const isNegative = minutes < 0;
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;

    let displayValue = "0m";
    if (hours > 0) {
        displayValue = mins > 0 ? `${hours}h${mins}m` : `${hours}h`;
    } else if (mins > 0) {
        displayValue = `${mins}m`;
    }

    return isNegative && displayValue !== "0m" ? `-${displayValue}` : displayValue;
}

export function formatOperationRemark(item = {}) {
    return (
        normalizeRemark(item.remarks) ||
        readAdditionalInfoRemark(item.additional_info) ||
        normalizeRemark(item.delay_reason) ||
        ""
    );
}

function normalizeGroupingValue(value) {
    if (value === null || value === undefined || value === "") return null;
    const normalizedValue = String(value).trim();
    return normalizedValue || null;
}

function readAdditionalInfoField(item, fieldName) {
    const additionalInfo = item?.additional_info;
    if (!additionalInfo || typeof additionalInfo !== "object") return undefined;
    return additionalInfo[fieldName];
}

function getOperationAircraftGroupKey(item = {}) {
    return normalizeGroupingValue(item.aircraft_reg ?? item.aircraftReg ?? item.aircraft) || "UNASSIGNED";
}

function getOperationRotationGroupKey(item = {}) {
    const rotationNo = normalizeGroupingValue(
        item.rotation_no ??
        item.rotationNo ??
        item.rotation_id ??
        item.rotationId ??
        readAdditionalInfoField(item, "rotation_no") ??
        readAdditionalInfoField(item, "rotationNo"),
    );
    if (rotationNo) return `rotation:${rotationNo}`;

    const rotationIndex = normalizeGroupingValue(
        item.rotation_index ??
        item.rotationIndex ??
        readAdditionalInfoField(item, "rotation_index") ??
        readAdditionalInfoField(item, "rotationIndex"),
    );
    if (rotationIndex) return `rotation-index:${rotationIndex}`;

    return null;
}

function hasOperationRowGapAfter(item, nextItem, hasRotationGrouping) {
    if (!nextItem) return !hasRotationGrouping;

    const aircraftChanged = getOperationAircraftGroupKey(item) !== getOperationAircraftGroupKey(nextItem);
    if (aircraftChanged) return true;
    if (!hasRotationGrouping) return false;

    const currentRotationGroupKey = getOperationRotationGroupKey(item);
    const nextRotationGroupKey = getOperationRotationGroupKey(nextItem);
    return Boolean(currentRotationGroupKey && nextRotationGroupKey && currentRotationGroupKey !== nextRotationGroupKey);
}

function projectOperationLegRow(item, rowGapAfter, columnBlocks = OPERATION_COLUMN_BLOCKS) {
    const operationItems = item.items || {};
    const row = {
        row_id: String(item.leg_id),
        leg_id: item.leg_id,
        aircraft: item.aircraft_reg,
        date: formatOperationDate(item.flight_date),
        day: formatOperationDay(item.flight_date),
        flight: item.flight_no || "",
        dep: item.dep || "",
        arr: item.arr || "",
        std: formatOperationTime(item.std),
        sta: formatOperationTime(item.sta),
        status: item.status || "",
        bh: formatBlockTimeMinutes(item.block_time_minutes),
        gt: formatGroundTimeMinutes(item.actual_ground_time_minutes),
        remark: formatOperationRemark(item),
        leg: formatOperationLegLabel(item),
        ...(rowGapAfter ? { _rowGapAfter: true } : {}),
        raw: item,
        items: operationItems,
    };

    columnBlocks.forEach((block) => {
        const category = block.category || block.transactionType;
        const operationItem = getPrimaryOperationItem(operationItems, category);
        (block.columns || []).forEach((field) => {
            row[`${block.transactionType}.${field.key}`] = readOperationItemField(operationItem, field.key);
        });
    });

    return row;
}

export function projectOperationLegRows(items = [], previousProjection = null, columnBlocks = OPERATION_COLUMN_BLOCKS) {
    const hasRotationGrouping = items.some((item) => getOperationRotationGroupKey(item));
    const previousItems = previousProjection?.items || [];
    const previousRows = previousProjection?.rows || [];
    const canReuseColumnProjection = previousProjection?.columnBlocks === columnBlocks;

    return items.map((item, index) => {
        const rowGapAfter = hasOperationRowGapAfter(item, items[index + 1], hasRotationGrouping);
        const previousRow = previousRows[index];
        if (canReuseColumnProjection && previousItems[index] === item && previousRow && Boolean(previousRow._rowGapAfter) === rowGapAfter) {
            return previousRow;
        }

        return projectOperationLegRow(item, rowGapAfter, columnBlocks);
    });
}

function parseUtcDateParts(value) {
    if (!value || typeof value !== "string") return null;
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { year, month, day };
}

function normalizeMinutes(value) {
    if (value === null || value === undefined || value === "") return null;
    const minutes = Number(value);
    if (!Number.isFinite(minutes)) return null;
    return Math.trunc(minutes);
}

function normalizeRemark(value) {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value);
    return "";
}

function readAdditionalInfoRemark(additionalInfo) {
    const directValue = normalizeRemark(additionalInfo);
    if (directValue) return directValue;
    if (!additionalInfo || typeof additionalInfo !== "object") return "";
    return (
        normalizeRemark(additionalInfo.remark) ||
        normalizeRemark(additionalInfo.remarks) ||
        normalizeRemark(additionalInfo.note) ||
        normalizeRemark(additionalInfo.comment) ||
        normalizeRemark(additionalInfo.delay_reason) ||
        ""
    );
}

export function getPrimaryOperationItem(itemsByCategory, category) {
    const categoryItems = itemsByCategory?.[category];
    return Array.isArray(categoryItems) && categoryItems.length > 0 ? categoryItems[0] : null;
}

export function readOperationItemField(item, fieldKey) {
    if (!item) return null;
    if (fieldKey === "status") return item.status;
    if (fieldKey === "remarks") return item.remarks ?? null;
    if (fieldKey === "order_id") return item.order_id ?? null;
    return isPlainObject(item.attributes) ? item.attributes[fieldKey] ?? null : null;
}

export function buildOperationCellKey(legId, category, fieldKey) {
    return `${legId}:${category}:${fieldKey}`;
}

export function getBlockByCategory(category, columnBlocks = OPERATION_COLUMN_BLOCKS) {
    return columnBlocks.find((block) => block.category === category || block.transactionType === category) || null;
}

export function getFieldLabel(block, fieldKey) {
    return block?.columns?.find((field) => field.key === fieldKey)?.label || fieldKey;
}
